import React, { useEffect, useRef } from "react";

type Props = {
  html: string;        // входной HTML
  baseStyles?: string; // дополнительные базовые стили (опционально)
};

export default function HtmlInShadow({ html, baseStyles = "" }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const shadowRef = useRef<ShadowRoot | null>(null);

  useEffect(() => {
    if (!hostRef.current) return;
    if (!shadowRef.current) {
      shadowRef.current = hostRef.current.attachShadow({ mode: "open" });
    }
    const shadow = shadowRef.current;

    // 1) Парсим входной HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // 2) Определяем контент и стили body (если есть)
    const bodyEl = doc.body;
    const hasBodyTag = bodyEl && bodyEl.childNodes.length > 0;
    const contentHTML = hasBodyTag ? bodyEl.innerHTML : html;

    // Собираем стили из <body> (style / bgcolor)
    const bodyCssText = hasBodyTag ? (bodyEl.getAttribute("style") || "") : "";
    const bodyBgColor = hasBodyTag ? (bodyEl.getAttribute("bgcolor") || "") : "";

    // 3) Готовим Shadow DOM: чистим и создаём style + wrapper
    shadow.innerHTML = "";

    const styleTag = document.createElement("style");
    styleTag.textContent = `
      /* изоляция и базовые стили только внутри Shadow DOM */
      :host {
        /* делаем шэдоу-блок блочным контейнером */
        display: block;
      }
      .message-root {
        white-space: pre-wrap;
        word-break: break-word;
        /* чтобы фон был виден на всей ширине блока сообщения */
        width: 100%;
        box-sizing: border-box;
      }
      /* легкие стили по умолчанию */
      a { text-decoration: underline; }
      img { max-width: 100%; height: auto; display: inline-block; }
      table { border-collapse: collapse; max-width: 100%; }
      th, td { border: 1px solid #ddd; padding: 4px 6px; }
      pre, code {
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace;
      }
      ${baseStyles}
    `;

    const wrapper = document.createElement("div");
    wrapper.className = "message-root";
    wrapper.innerHTML = contentHTML;

    // 4) Переносим стили body на wrapper
    //    - inline style -> cssText
    //    - bgcolor -> background-color (если есть)
    if (bodyCssText) {
      wrapper.style.cssText += ";" + bodyCssText;
    }
    if (bodyBgColor && !wrapper.style.backgroundColor) {
      wrapper.style.backgroundColor = bodyBgColor;
    }

    // 5) Встраиваем в Shadow DOM
    shadow.appendChild(styleTag);
    shadow.appendChild(wrapper);

  }, [html, baseStyles]);

  return <div ref={hostRef} />;
}
